/* eslint no-console: 0 */

// Severity ordering for LOG_LEVEL filtering. A call is emitted only when its own
// severity is at or below the configured threshold, so LOG_LEVEL=info keeps
// error/warning/info and drops debug.
const LEVELS = { error: 0, warn: 1, info: 2, debug: 3 };

// Threshold used when LOG_LEVEL is unset or is not one of the names above. `info`
// makes debug output opt-in, which is what quiets the ~950 logDebug call sites in
// production: deployed lambdas take their whole environment from env.yml (generated
// from SSM), so LOG_LEVEL is absent there and this default applies with no
// infrastructure change. Set LOG_LEVEL=debug on an environment to get it back.
const DEFAULT_LEVEL = 'info';

// `process.env.LOG_LEVEL` is written as that exact literal expression on purpose:
// webpack's DefinePlugin substitutes it at build time in the control-room and `my`
// bundles, so it never becomes a runtime `process` lookup in the browser. The typeof
// guard is the fallback for any bundle that leaves the expression intact — `my` has
// no NodePolyfillPlugin, so an unguarded read would throw "process is not defined".
const threshold = () => {
  const configured =
    typeof process === 'undefined' || !process.env ? '' : (process.env.LOG_LEVEL || '').toLowerCase();

  // Own-property check rather than an undefined comparison: inherited names such as
  // `constructor` or `toString` would otherwise resolve through Object.prototype and
  // yield a function, which every severity comparison then reads as false.
  return Object.prototype.hasOwnProperty.call(LEVELS, configured) ? LEVELS[configured] : LEVELS[DEFAULT_LEVEL];
};

const enabled = (level) => LEVELS[level] <= threshold();

const root = {
  // Errors are emitted at every threshold — error is the lowest severity, so it is
  // deliberately left unguarded rather than relying on the comparison above.
  logError: (params) => {
    const errorMessage = params.error ? params.error.stack : params.message;
    return root.log({ message: `Error: ${errorMessage}` });
  },
  logWarning: (params) =>
    enabled('warn') ? root.log({ message: `Warning: ${params.message}` }) : Promise.resolve(),
  logDebug: (params) => (enabled('debug') ? root.log({ message: `Debug: ${params.message}` }) : Promise.resolve()),
  logInfo: (params) => (enabled('info') ? root.log({ message: `Info: ${params.message}` }) : Promise.resolve()),
  // The raw transport, not part of the severity API: it writes unconditionally and the
  // level methods above delegate to it (which is also what lets specs swap it for a spy).
  // Call logError/logWarning/logInfo/logDebug instead — calling this directly bypasses
  // LOG_LEVEL entirely.
  log: (params) =>
    new Promise((resolve) => {
      console.log(params.message);
      resolve();
    }),
};

export default root;
